
import type { PropType } from 'vue'
import { defineComponent, onBeforeMount, reactive, watch } from 'vue'

// > components <
import CalendarButton from './CalendarButton.vue'
import CalendarControl from './CalendarControl.vue'

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

export default defineComponent({
    components: {
        CalendarButton,
        CalendarControl
    },
    props: {
        startFrom: {
            type: String as PropType<'year' | 'month' | 'date'>,
            default: 'date'
        },
        value: {
            type: String as PropType<string | null>,
            default: null
        },
        yearsForSelectCount: {
            type: Number,
            default: 9
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        outputFormat: {
            type: String as PropType<'YYYY-MM-DD' | 'DD.MM.YYYY'>,
            default: 'YYYY-MM-DD'
        },
    },
    emits: ['update', 'change'],
    setup(props, { emit }) {
        const view = reactive({
            currentYear: new Date(Date.now()).getFullYear(),
            currentMonth: new Date(Date.now()).getMonth(),
            //
            step: props.startFrom,
            selectedYear: null as number | null,
            selectedMonth: null as number | null,
            dates: [] as Date[]
        })

        const onSelectYear = (year: number) => {
            if (props.disabled) return

            view.selectedYear = year
            view.step = 'month'
        }

        const onSelectMonth = (month: number) => {
            if (props.disabled) return

            view.selectedMonth = month
            view.step = 'date'
        }

        const onSelectPreviousMonth = () => {
            const year = view.selectedYear as number
            const month = view.selectedMonth as number

            if (month === 0) {
                view.selectedMonth = 11
                view.selectedYear = year - 1

                return
            }

            view.selectedMonth = month - 1
        }

        const onSelectNextMonth = () => {
            const year = view.selectedYear as number
            const month = view.selectedMonth as number

            if (month === 11) {
                view.selectedMonth = 0
                view.selectedYear = year + 1

                return
            }

            view.selectedMonth = month + 1
        }

        const generateDays = (year: number, month: number) => {
            const dates: Date[] = []

            const firstDayOfMonth = new Date(year, month, 1, 0, 0, 0, 0)

            const startWeekDay = (firstDayOfMonth.getDay() + 6) % 7
            const daysInMonth = new Date(year, month + 1, 0).getDate()

            for (let i = startWeekDay - 1; i >= 0; i--) {
                dates.push(new Date(year, month, -i, 0, 0, 0, 0))
            }

            for (let day = 1; day <= daysInMonth; day++) {
                dates.push(new Date(year, month, day, 0, 0, 0, 0))
            }

            const remaining = (7 - (dates.length % 7)) % 7

            for (let i = 1; i <= remaining; i++) {
                dates.push(new Date(year, month + 1, i, 0, 0, 0, 0))
            }

            return dates
        }

        const formatDate = (date: Date) => {
            const yyyy = date.getFullYear()
            const mm = String(date.getMonth() + 1).padStart(2, '0')
            const dd = String(date.getDate()).padStart(2, '0')

            if (props.outputFormat === 'DD.MM.YYYY') {
                return `${dd}.${mm}.${yyyy}`
            }

            return `${yyyy}-${mm}-${dd}`
        }

        const isDateSelected = (date: Date) => {
            if (!props.value) return false
            return formatDate(date) === props.value
        }

        const onUpdate = (date: Date) => {
            if (props.disabled) return

            emit('update', formatDate(date))
            emit('change', formatDate(date))
        }

        onBeforeMount(() => {
            if (props.startFrom === 'month' || props.startFrom === 'date') {
                view.selectedYear = view.currentYear
            }

            if (props.startFrom === 'date') {
                view.selectedMonth = view.currentMonth
            }
        })

        watch(
            () => [view.selectedYear, view.selectedMonth],
            ([year, month]) => {
                if (year !== null && month !== null) {
                    view.dates = generateDays(year, month)
                }
            },
            { immediate: true }
        )

        return {
            view,
            onSelectYear,
            onSelectMonth,
            onSelectPreviousMonth,
            onSelectNextMonth,
            isDateSelected,
            onUpdate,
            MONTHS,
        }
    }
})
